import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SectionHeading,
  SubHeading,
  Desc,
  SettingRow,
  LabelTitle,
  LabelDesc,
  Control,
  StyledSelect,
  SettingsCard,
  CardHeader,
  CardTitle,
  CardGrid,
  CardGridItem,
  EditButton,
} from '../shared/primitives';
import ActiveDeterrenceEdit, { type DeterrenceSettings as EditSettings } from './ActiveDeterrenceEditModal';
import styles from './DeterrenceSection.module.css';

export interface DeterrenceSettings {
  name: string;
  lightStyle: string;
  soundEffect: string;
  soundVolume: number;
  timeout: string;
}

interface DeterrenceSectionProps {
  onPassiveDeterrenceChange?: (value: string, settings: DeterrenceSettings) => void;
  onManualTriggerChange?: (value: string) => void;
  onActiveDeterrenceEdit?: () => void;
  onActiveDeterrenceSettingsChange?: (settings: DeterrenceSettings) => void;
}

const DeterrenceSection: React.FC<DeterrenceSectionProps> = ({
  onPassiveDeterrenceChange,
  onManualTriggerChange,
  onActiveDeterrenceEdit,
  onActiveDeterrenceSettingsChange,
}) => {
  const { t } = useTranslation();

  // Build translated options
  const getLightStyleLabel = (value: string) => t(`bulkEditSettings.deterrence.lightStyles.${value}`, value);
  const getSoundEffectLabel = (value: string) => t(`bulkEditSettings.deterrence.soundEffects.${value}`, value);
  const getTimeoutLabel = (value: string) => t(`bulkEditSettings.deterrence.timeoutOptions.${value}`, value);

  // Derive option keys from locale objects so there's a single source of truth
  const lightStylesObj = t('bulkEditSettings.deterrence.lightStyles', { returnObjects: true }) as Record<string, string>;
  const soundEffectsObj = t('bulkEditSettings.deterrence.soundEffects', { returnObjects: true }) as Record<string, string>;
  const timeoutOptionsObj = t('bulkEditSettings.deterrence.timeoutOptions', { returnObjects: true }) as Record<string, string>;

  const LIGHT_STYLE_VALUES = Object.keys(lightStylesObj);
  const SOUND_EFFECT_VALUES = Object.keys(soundEffectsObj);
  const TIMEOUT_VALUES = Object.keys(timeoutOptionsObj);

  const [passiveDeterrence, setPassiveDeterrence] = useState(t('common.disabled'));
  const [manualTrigger, setManualTrigger] = useState(t('bulkEditSettings.deterrence.behaviorNames.Default'));
  const [isEditing, setIsEditing] = useState(false);
  const [deterrenceSettings, setDeterrenceSettings] = useState<DeterrenceSettings>({
    name: 'Default',
    lightStyle: 'ColorStrobe',
    soundEffect: 'Disable',
    soundVolume: 10,
    timeout: '30s',
  });

  const handlePassiveChange = (value: string) => {
    setPassiveDeterrence(value);
    onPassiveDeterrenceChange?.(value, deterrenceSettings);
  };

  const handleManualTriggerChange = (value: string) => {
    setManualTrigger(value);
    onManualTriggerChange?.(value);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    onActiveDeterrenceEdit?.();
  };

  const handleEditClose = () => {
    setIsEditing(false);
  };

  const handleEditSave = (settings: EditSettings) => {
    const newSettings: DeterrenceSettings = {
      name: deterrenceSettings.name,
      ...settings,
    };
    setDeterrenceSettings(newSettings);
    onActiveDeterrenceSettingsChange?.(newSettings);
    setIsEditing(false);
  };

  // Edit View - use separate component
  if (isEditing) {
    return (
      <ActiveDeterrenceEdit
        isOpen={isEditing}
        onClose={handleEditClose}
        onSave={handleEditSave}
        initialSettings={deterrenceSettings}
      />
    );
  }

  // Main View
  return (
    <>
      <SectionHeading>{t('bulkEditSettings.deterrence.heading')}</SectionHeading>

      {/* Passive Deterrence */}
      <SettingRow>
        <div>
          <LabelTitle>{t('bulkEditSettings.deterrence.passiveDeterrence.label')}</LabelTitle>
          <LabelDesc>{t('bulkEditSettings.deterrence.passiveDeterrence.description')}</LabelDesc>
        </div>
        <Control>
          <StyledSelect name="passiveDeterrence" value={passiveDeterrence} onChange={e => handlePassiveChange(e.target.value)}>
            <option value={t('common.disabled')}>{t('common.disabled')}</option>
            <option value={t('common.enabled')}>{t('common.enabled')}</option>
          </StyledSelect>
        </Control>
      </SettingRow>

      {/* Active Deterrence Behaviors */}
      <div className={styles.activeDeterrenceSection}>
        <SubHeading>{t('bulkEditSettings.deterrence.activeDeterrence.heading')}</SubHeading>
        <Desc>{t('bulkEditSettings.deterrence.activeDeterrence.description')}</Desc>
      </div>

      <SettingRow>
        <div>
          <LabelTitle>{t('bulkEditSettings.deterrence.manualTrigger.label')}</LabelTitle>
          <LabelDesc>{t('bulkEditSettings.deterrence.manualTrigger.description')}</LabelDesc>
        </div>
        <Control>
          <StyledSelect name="manualTrigger" value={manualTrigger} onChange={e => handleManualTriggerChange(e.target.value)}>
            <option value={t('bulkEditSettings.deterrence.behaviorNames.Default')}>{t('bulkEditSettings.deterrence.behaviorNames.Default')}</option>
          </StyledSelect>
        </Control>
      </SettingRow>

      {/* Settings Card */}
      <SettingsCard>
        <CardHeader>
          <CardTitle>{t(`bulkEditSettings.deterrence.behaviorNames.${deterrenceSettings.name}`, deterrenceSettings.name)}</CardTitle>
          <EditButton onClick={handleEditClick} aria-label={t('common.edit')} />
        </CardHeader>
        <CardGrid>
          <CardGridItem label={t('bulkEditSettings.deterrence.card.lightStyle')} value={getLightStyleLabel(deterrenceSettings.lightStyle)} />
          <CardGridItem label={t('bulkEditSettings.deterrence.card.sound')} value={getSoundEffectLabel(deterrenceSettings.soundEffect)} />
          <CardGridItem label={t('bulkEditSettings.deterrence.card.volume')} value={String(deterrenceSettings.soundVolume)} />
          <CardGridItem label={t('bulkEditSettings.deterrence.card.timeout')} value={getTimeoutLabel(deterrenceSettings.timeout)} />
        </CardGrid>
      </SettingsCard>
    </>
  );
};

export default DeterrenceSection;
